import { useEffect, useRef } from "react";

/**
 * usePageMeta — lightweight per-page SEO helper.
 *   - Sets document.title (with " | SRM …" suffix)
 *   - Updates <meta name="description"> + <meta name="keywords">
 *   - Updates Open Graph + Twitter title/description/url/image
 *   - Maintains <link rel="canonical">
 *   - Injects optional BreadcrumbList JSON-LD
 *
 * The effect intentionally depends on a single memo-key derived from the
 * options object so the dependency count stays at 1 — additional individual
 * deps would cause unnecessary re-renders without changing behaviour.
 */
const SITE_URL = "https://strataroofmanagement.com.au";
const SITE_OG_IMAGE = `${SITE_URL}/assets/srm-logo.png`;
const BREADCRUMB_SCRIPT_ID = "page-breadcrumb-jsonld";

function ensureMeta(selector, factory, content) {
    if (!content) return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = factory();
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function ensureLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function applyMeta({ title, description, keywords, path, breadcrumbs, ogImage, ogTitle, ogDescription, ogType, articleSchema }) {
    // Title is used as-is. Each page is responsible for keeping its own
    // <title> under ~60 characters (no global suffix is appended here —
    // appending one would push every page over the SEO truncation limit).
    const fullTitle = title || null;
    if (fullTitle) document.title = fullTitle;

    const canonical = path ? `${SITE_URL}${path}` : SITE_URL;
    const image = ogImage || SITE_OG_IMAGE;
    const socialTitle = ogTitle || fullTitle;
    const socialDescription = ogDescription || description;

    ensureMeta('meta[name="description"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
    }, description);

    ensureMeta('meta[name="keywords"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "keywords");
        return m;
    }, keywords);

    ensureMeta('meta[property="og:title"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:title");
        return m;
    }, socialTitle);

    ensureMeta('meta[property="og:description"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:description");
        return m;
    }, socialDescription);

    ensureMeta('meta[property="og:url"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:url");
        return m;
    }, canonical);

    ensureMeta('meta[property="og:image"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:image");
        return m;
    }, image);

    ensureMeta('meta[property="og:type"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:type");
        return m;
    }, ogType || "website");

    ensureMeta('meta[name="twitter:card"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:card");
        return m;
    }, "summary_large_image");

    ensureMeta('meta[name="twitter:title"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:title");
        return m;
    }, socialTitle);

    ensureMeta('meta[name="twitter:description"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:description");
        return m;
    }, socialDescription);

    ensureMeta('meta[name="twitter:image"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:image");
        return m;
    }, image);

    ensureLink("canonical", canonical);

    const existing = document.getElementById(BREADCRUMB_SCRIPT_ID);
    if (existing) existing.remove();

    if (breadcrumbs && breadcrumbs.length > 0) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = BREADCRUMB_SCRIPT_ID;
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: b.name,
                item: `${SITE_URL}${b.url}`,
            })),
        });
        document.head.appendChild(script);
    }

    /* Optional Article structured data — injected only when the caller
       supplies articleSchema. Uses SITE_URL + path for the mainEntityOfPage. */
    const ARTICLE_SCRIPT_ID = "page-article-jsonld";
    const existingArticle = document.getElementById(ARTICLE_SCRIPT_ID);
    if (existingArticle) existingArticle.remove();

    if (articleSchema) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = ARTICLE_SCRIPT_ID;
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: articleSchema.headline,
            description: articleSchema.description,
            author: {
                "@type": "Person",
                name: articleSchema.authorName,
                jobTitle: articleSchema.authorRole,
                worksFor: { "@id": `${SITE_URL}/#organization` },
            },
            datePublished: articleSchema.datePublished,
            image: articleSchema.image
                ? articleSchema.image.startsWith("http")
                    ? articleSchema.image
                    : `${SITE_URL}${articleSchema.image}`
                : image,
            publisher: {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "Strata Roof Management Australia",
                url: `${SITE_URL}/`,
                logo: {
                    "@type": "ImageObject",
                    url: SITE_OG_IMAGE,
                },
            },
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": canonical,
            },
        });
        document.head.appendChild(script);
    }
}

/**
 * Build a stable string key for the options so the useEffect only re-runs
 * when meaningful content changes — not on every parent re-render.
 */
function keyFor(options) {
    return JSON.stringify({
        t: options.title || null,
        d: options.description || null,
        k: options.keywords || null,
        p: options.path || null,
        b: options.breadcrumbs || null,
        i: options.ogImage || null,
        ot: options.ogTitle || null,
        od: options.ogDescription || null,
        ty: options.ogType || null,
        a: options.articleSchema || null,
    });
}

export default function usePageMeta(options) {
    // Hold a ref to the latest options so the effect callback can read
    // them without listing each field as an individual dependency.
    const latest = useRef(options);
    latest.current = options;

    const key = keyFor(options);

    useEffect(() => {
        applyMeta(latest.current);
    }, [key]);
}
